// ============================================
// LAYOUT ADMINISTRATIVO
// src/components/admin/AdminLayout.tsx
// ============================================

import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

export function AdminLayout() {
  const { admin, logout, hasPermission } = useAdminAuth();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  async function handleLogout() {
    await logout();
    navigate('/login-admin');
  }

  return (
    <div className="d-flex" style={{ minHeight: '100vh' }}>
      {/* Sidebar */}
      <aside 
        className={`bg-dark text-white d-flex flex-column ${sidebarCollapsed ? 'collapsed' : ''}`}
        style={{ 
          width: sidebarCollapsed ? '70px' : '260px',
          transition: 'width 0.3s ease',
          position: 'fixed',
          top: 0,
          bottom: 0,
          left: 0,
          zIndex: 1000
        }}
      >
        {/* Logo */}
        <div 
          className="d-flex align-items-center justify-content-between p-3 border-bottom border-secondary"
          style={{ minHeight: '64px' }}
        >
          {!sidebarCollapsed && (
            <div className="d-flex align-items-center">
              <i className="fas fa-shield-alt fa-lg text-info me-2"></i>
              <span className="fw-bold">Admin Panel</span>
            </div>
          )}
          <button 
            className="btn btn-sm btn-outline-light border-0"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            <i className={`fas fa-${sidebarCollapsed ? 'angles-right' : 'angles-left'}`}></i>
          </button>
        </div>

        {/* Info do Admin */}
        {!sidebarCollapsed && admin && (
          <div className="p-3 border-bottom border-secondary">
            <div className="d-flex align-items-center">
              <div 
                className="rounded-circle bg-info d-flex align-items-center justify-content-center me-3"
                style={{ width: '40px', height: '40px' }}
              >
                <i className="fas fa-user text-white"></i>
              </div>
              <div>
                <div className="fw-semibold text-truncate" style={{ maxWidth: '150px' }}>
                  {admin.nome}
                </div>
                <small className={`badge ${
                  admin.nivel === 'super_admin' ? 'bg-danger' :
                  admin.nivel === 'admin' ? 'bg-warning text-dark' :
                  'bg-info'
                }`}>
                  {admin.nivel}
                </small>
              </div>
            </div>
          </div>
        )}

        {/* Menu de Navegação */}
        <nav className="flex-grow-1 py-3">
          <ul className="nav flex-column">
            {/* Dashboard */}
            <li className="nav-item">
              <NavLink 
                to="/admin/dashboard" 
                className={({ isActive }) => 
                  `nav-link text-white d-flex align-items-center px-3 py-2 ${isActive ? 'bg-primary' : 'hover-bg'}`
                }
                title="Dashboard"
              >
                <i className="fas fa-tachometer-alt" style={{ width: '24px' }}></i>
                {!sidebarCollapsed && <span className="ms-3">Dashboard</span>}
              </NavLink>
            </li>

            {/* Empresas */}
            {hasPermission('visualizar_empresas') && (
              <li className="nav-item">
                <NavLink 
                  to="/admin/empresas" 
                  className={({ isActive }) => 
                    `nav-link text-white d-flex align-items-center px-3 py-2 ${isActive ? 'bg-primary' : 'hover-bg'}`
                  }
                  title="Empresas"
                >
                  <i className="fas fa-building" style={{ width: '24px' }}></i>
                  {!sidebarCollapsed && <span className="ms-3">Empresas</span>}
                </NavLink>
              </li>
            )}

            {/* Usuários */}
            {hasPermission('visualizar_usuarios') && (
              <li className="nav-item">
                <NavLink 
                  to="/admin/usuarios" 
                  className={({ isActive }) => 
                    `nav-link text-white d-flex align-items-center px-3 py-2 ${isActive ? 'bg-primary' : 'hover-bg'}`
                  }
                  title="Usuários"
                >
                  <i className="fas fa-users" style={{ width: '24px' }}></i>
                  {!sidebarCollapsed && <span className="ms-3">Usuários</span>}
                </NavLink>
              </li>
            )}

            {/* Auditoria */}
            {hasPermission('visualizar_logs') && (
              <li className="nav-item">
                <NavLink 
                  to="/admin/auditoria" 
                  className={({ isActive }) => 
                    `nav-link text-white d-flex align-items-center px-3 py-2 ${isActive ? 'bg-primary' : 'hover-bg'}`
                  }
                  title="Auditoria"
                >
                  <i className="fas fa-history" style={{ width: '24px' }}></i>
                  {!sidebarCollapsed && <span className="ms-3">Auditoria</span>}
                </NavLink>
              </li>
            )}

            {/* Pesquisas */}
            {hasPermission('gerenciar_configuracoes') && (
              <li className="nav-item">
                <NavLink 
                  to="/admin/pesquisas" 
                  className={({ isActive }) => 
                    `nav-link text-white d-flex align-items-center px-3 py-2 ${isActive ? 'bg-primary' : 'hover-bg'}`
                  }
                  title="Pesquisas"
                >
                  <i className="fas fa-poll" style={{ width: '24px' }}></i>
                  {!sidebarCollapsed && <span className="ms-3">Pesquisas</span>}
                </NavLink>
              </li>
            )}

            {/* Comunicação */}
            {hasPermission('gerenciar_configuracoes') && (
              <li className="nav-item">
                <NavLink 
                  to="/admin/comunicacao" 
                  className={({ isActive }) => 
                    `nav-link text-white d-flex align-items-center px-3 py-2 ${isActive ? 'bg-primary' : 'hover-bg'}`
                  }
                  title="Comunicação"
                >
                  <i className="fas fa-bullhorn" style={{ width: '24px' }}></i>
                  {!sidebarCollapsed && <span className="ms-3">Comunicação</span>}
                </NavLink>
              </li>
            )}

            {/* Separador */}
            <li className="nav-item my-2">
              <hr className="border-secondary mx-3" />
            </li>

            {/* Gerenciar Admins (apenas super_admin) */}
            {hasPermission('gerenciar_admins') && (
              <li className="nav-item">
                <NavLink 
                  to="/admin/administradores" 
                  className={({ isActive }) => 
                    `nav-link text-white d-flex align-items-center px-3 py-2 ${isActive ? 'bg-primary' : 'hover-bg'}`
                  }
                  title="Administradores"
                >
                  <i className="fas fa-user-shield" style={{ width: '24px' }}></i>
                  {!sidebarCollapsed && <span className="ms-3">Administradores</span>}
                </NavLink>
              </li>
            )}

            {/* Configurações */}
            {hasPermission('gerenciar_configuracoes') && (
              <li className="nav-item">
                <NavLink 
                  to="/admin/configuracoes" 
                  className={({ isActive }) => 
                    `nav-link text-white d-flex align-items-center px-3 py-2 ${isActive ? 'bg-primary' : 'hover-bg'}`
                  }
                  title="Configurações"
                >
                  <i className="fas fa-cog" style={{ width: '24px' }}></i>
                  {!sidebarCollapsed && <span className="ms-3">Configurações</span>}
                </NavLink>
              </li>
            )}
          </ul>
        </nav>

        {/* Footer com Logout */}
        <div className="p-3 border-top border-secondary">
          <button 
            className="btn btn-outline-danger w-100 d-flex align-items-center justify-content-center"
            onClick={handleLogout}
            title="Sair"
          >
            <i className="fas fa-sign-out-alt"></i>
            {!sidebarCollapsed && <span className="ms-2">Sair</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main 
        className="flex-grow-1 bg-light"
        style={{ 
          marginLeft: sidebarCollapsed ? '70px' : '260px',
          transition: 'margin-left 0.3s ease'
        }}
      >
        {/* Top Header */}
        <header 
          className="bg-white shadow-sm d-flex align-items-center justify-content-between px-4"
          style={{ height: '64px' }}
        >
          <div className="d-flex align-items-center">
            <h5 className="mb-0 text-muted">
              <i className="fas fa-shield-alt text-primary me-2"></i>
              Sistema Administrativo Sophya
            </h5>
          </div>
          <div className="d-flex align-items-center gap-3">
            {/* Notificações (placeholder) */}
            <button className="btn btn-light position-relative" title="Notificações">
              <i className="fas fa-bell"></i>
              <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                0
              </span>
            </button>
            
            {/* Info Admin */}
            <div className="d-flex align-items-center">
              <span className="me-2 d-none d-md-inline">{admin?.nome}</span>
              <div 
                className="rounded-circle bg-primary d-flex align-items-center justify-content-center"
                style={{ width: '35px', height: '35px' }}
              >
                <i className="fas fa-user text-white small"></i>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4">
          <Outlet />
        </div>

        {/* Footer */}
        <footer className="bg-white border-top text-center py-3 mt-auto">
          <small className="text-muted">
            © {new Date().getFullYear()} Sistema Sophya - Painel Administrativo
          </small>
        </footer>
      </main>

      {/* CSS para hover nos links */}
      <style>{`
        .hover-bg:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }
        .nav-link.active {
          background-color: var(--bs-primary) !important;
        }
      `}</style>
    </div>
  );
}

export default AdminLayout;
